import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0,0,0,0.3)',
    }
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'var(--bg-elev-2)',
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '0.95rem',
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
    padding: '14px 8px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'var(--bg-muted)',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableCell-body': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 8px',
    fontSize: '0.9rem',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 500,
}));

const GrowthRateCell = styled(TableCell)<{ positive?: boolean }>(({ theme, positive }) => ({
  textAlign: 'center',
  fontWeight: 600,
  color: positive !== undefined 
    ? (positive ? theme.palette.success.main : theme.palette.error.main)
    : theme.palette.text.primary,
}));

const GrowthMetricsSection: React.FC = () => {
  const theme = useTheme();
  const { dashboardData } = useAccountBookStore();

  // 전월 대비 (MoM) 계산
  const momData = useMemo(() => {
    const monthlyData = dashboardData.monthlyData;
    const mom: Array<{
      month: number;
      revenueGrowth: number | null;
      expenseGrowth: number | null;
      profitGrowth: number | null;
    }> = [];

    monthlyData.forEach((currentMonth, index) => {
      if (index === 0) {
        // 1월은 전월 데이터가 없으므로 null
        mom.push({
          month: currentMonth.month,
          revenueGrowth: null,
          expenseGrowth: null,
          profitGrowth: null,
        });
      } else {
        const previousMonth = monthlyData[index - 1];
        const calculateGrowth = (current: number, previous: number): number | null => {
          if (previous === 0) return current > 0 ? 100 : null;
          return Math.round(((current - previous) / previous) * 100 * 100) / 100;
        };

        mom.push({
          month: currentMonth.month,
          revenueGrowth: calculateGrowth(currentMonth.income, previousMonth.income),
          expenseGrowth: calculateGrowth(currentMonth.expense, previousMonth.expense),
          profitGrowth: calculateGrowth(currentMonth.netIncome, previousMonth.netIncome),
        });
      }
    });

    return mom;
  }, [dashboardData.monthlyData]);

  // 전년 동월 대비 (YoY) 계산
  // 현재는 previousYearKpiData만 있으므로, 전체적인 비교만 가능
  const yoyData = useMemo(() => {
    const currentYear = dashboardData.kpiData;
    const previousYear = dashboardData.previousYearKpiData;

    const calculateGrowth = (current: number, previous: number): number | null => {
      if (previous === 0) return current > 0 ? 100 : null;
      return Math.round(((current - previous) / previous) * 100 * 100) / 100;
    };

    return {
      revenueGrowth: calculateGrowth(currentYear.totalIncome, previousYear.totalIncome),
      expenseGrowth: calculateGrowth(currentYear.totalExpense, previousYear.totalExpense),
      profitGrowth: calculateGrowth(currentYear.netIncome, previousYear.netIncome),
    };
  }, [dashboardData.kpiData, dashboardData.previousYearKpiData]);

  const formatGrowthRate = (rate: number | null): string => {
    if (rate === null) return '-';
    return `${rate >= 0 ? '+' : ''}${rate.toFixed(2)}%`;
  };

  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 },
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-16)',
        border: '1px solid var(--primary-soft)',
      }}
    >
      <SectionHeader
        eyebrow="성장률"
        title="성장률 지표"
        subtitle={`${dashboardData.selectedYear}년 전월·전년 대비`}
      />

      <Grid container spacing={2}>
        {/* 전월 대비 (MoM) 테이블 */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            전월 대비 (MoM) 성장률
          </Typography>
          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>월</StyledTableCell>
                  <StyledTableCell>매출 성장률</StyledTableCell>
                  <StyledTableCell>지출 성장률</StyledTableCell>
                  <StyledTableCell>영업이익 성장률</StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {momData.map((item) => (
                  <StyledTableRow key={item.month}>
                    <StyledTableCell>{monthNames[item.month - 1]}</StyledTableCell>
                    <GrowthRateCell 
                      positive={item.revenueGrowth !== null && item.revenueGrowth >= 0}
                    >
                      {formatGrowthRate(item.revenueGrowth)}
                    </GrowthRateCell>
                    <GrowthRateCell 
                      positive={item.expenseGrowth !== null && item.expenseGrowth >= 0}
                    >
                      {formatGrowthRate(item.expenseGrowth)}
                    </GrowthRateCell>
                    <GrowthRateCell>
                      {formatGrowthRate(item.profitGrowth)}
                    </GrowthRateCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Grid>

        {/* 전년 대비 (YoY) 테이블 */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            전년 대비 (YoY) 성장률
          </Typography>
          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>항목</StyledTableCell>
                  <StyledTableCell>성장률</StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell>매출</StyledTableCell>
                  <GrowthRateCell 
                    positive={yoyData.revenueGrowth !== null && yoyData.revenueGrowth >= 0}
                  >
                    {formatGrowthRate(yoyData.revenueGrowth)}
                  </GrowthRateCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>지출</StyledTableCell>
                  <GrowthRateCell 
                    positive={yoyData.expenseGrowth !== null && yoyData.expenseGrowth >= 0}
                  >
                    {formatGrowthRate(yoyData.expenseGrowth)}
                  </GrowthRateCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>영업이익</StyledTableCell>
                  <GrowthRateCell>
                    {formatGrowthRate(yoyData.profitGrowth)}
                  </GrowthRateCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default GrowthMetricsSection;

